/**
 * Iter-291 — Talent QR scan landing page.
 *
 * Route: /talent/qr-scan
 *
 * Flow:
 *   1. Scanner modal opens automatically on mount.
 *   2. User scans → we POST /attendance/qr/lookup to resolve the token to
 *      either an event series or a single shift, plus the talent's booking.
 *   3. If we find a booking, we deep-link to /talent/checkin/:bookingId
 *      with `?qr=<token>` so the existing check-in screen auto-fills the
 *      QR token field.
 *   4. If no booking exists for this talent, show a friendly error.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, QrCode, AlertTriangle, Loader2 } from 'lucide-react';
import QRScannerModal from '../../components/QRScannerModal';

const TalentQRScan = () => {
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [lastToken, setLastToken] = useState('');

  const handleScan = async (rawText) => {
    setScannerOpen(false);
    setResolving(true);
    setError(null);
    // QR payload is either a raw token or "ieventur-checkin:<token>"
    const token = rawText.startsWith('ieventur-checkin:') ? rawText.slice(17) : rawText.trim();
    setLastToken(token);
    try {
      const r = await api.get(`/attendance/qr/lookup/${encodeURIComponent(token)}`);
      const { booking_id, action, event_title } = r.data;
      if (!booking_id) {
        setError(`No active booking found for this QR (${event_title || 'event'}). Please confirm with the organizer.`);
        return;
      }
      // Hand off to CheckIn screen with token pre-filled.
      navigate(`/talent/checkin/${booking_id}?qr=${encodeURIComponent(token)}&action=${action || 'clock_in'}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not recognize that QR code.');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white" data-testid="talent-qr-scan-page">
      <div className="max-w-md mx-auto p-4 sm:p-6 space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
          data-testid="qr-scan-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-cyan-600" /> Scan event QR</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Scan the QR code displayed at your event to clock in or clock out.
            </p>
            {resolving && (
              <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="qr-resolving">
                <Loader2 className="w-4 h-4 animate-spin" /> Resolving QR…
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="qr-scan-error">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { setError(null); setScannerOpen(true); }} data-testid="qr-scan-open">
                <QrCode className="w-4 h-4 mr-1.5" /> {error ? 'Try again' : 'Open scanner'}
              </Button>
              {lastToken && (
                <span className="text-xs text-slate-400 self-center font-mono break-all">last: {lastToken}</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <QRScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScan} />
    </div>
  );
};

export default TalentQRScan;
